import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, Check, Eye, EyeOff, KeyRound, MailCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { ErrorBox, Field } from '../components/ui';

/** "Forgot password?": asks for the email and sends a reset link. */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const send = useMutation({ mutationFn: () => api.post<{ emailEnabled: boolean }>('/auth/forgot-password', { email: email.trim() }) });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    send.mutate();
  };
  return (
    <div className="login">
      {send.isSuccess ? (
        <div className="card stack">
          <img src="/pulseiptv-stacked.png" alt="PulseIPTV" />
          <span className="reset-icon">
            <MailCheck size={28} />
          </span>
          <h2 style={{ margin: 0, textAlign: 'center' }}>Check your email</h2>
          {send.data.emailEnabled ? (
            <p className="muted" style={{ margin: 0, textAlign: 'center' }}>
              If <strong>{email.trim()}</strong> has a PulseIPTV account, we sent it a link to choose a new password. The link works for 30 minutes.
            </p>
          ) : (
            <p className="muted" style={{ margin: 0, textAlign: 'center' }}>
              Email is not set up on this server yet, so no link could be sent. Ask an administrator to set a new password for you in Account settings › Users.
            </p>
          )}
          <Link to="/login" className="btn" style={{ justifyContent: 'center' }}>
            <ArrowLeft size={15} /> Back to sign in
          </Link>
        </div>
      ) : (
        <form className="card stack" onSubmit={submit}>
          <img src="/pulseiptv-stacked.png" alt="PulseIPTV" />
          <h2 style={{ margin: 0 }}>Forgot your password?</h2>
          <p className="muted" style={{ margin: 0 }}>
            Enter the email you sign in with. We send you a link to choose a new password.
          </p>
          <Field label="Email">
            <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </Field>
          <ErrorBox error={send.error} />
          <button className="btn primary" disabled={send.isPending || !email.trim()} type="submit" style={{ justifyContent: 'center' }}>
            {send.isPending ? 'Sending…' : 'Send reset link'}
          </button>
          <Link to="/login" className="login-link">
            <ArrowLeft size={14} /> Back to sign in
          </Link>
        </form>
      )}
    </div>
  );
}

/** The page the emailed link opens: choose a new password. */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [pw, setPw] = useState('');
  const [repeat, setRepeat] = useState('');
  const [show, setShow] = useState(false);
  const reset = useMutation({ mutationFn: () => api.post('/auth/reset-password', { token, password: pw }) });
  const mismatch = repeat.length > 0 && pw !== repeat;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (pw.length >= 10 && pw === repeat) reset.mutate();
  };
  if (!token)
    return (
      <div className="login">
        <div className="card stack">
          <img src="/pulseiptv-stacked.png" alt="PulseIPTV" />
          <p style={{ margin: 0 }}>This link is incomplete. Open the link from the email again, or ask for a new one.</p>
          <Link to="/forgot-password" className="btn primary" style={{ justifyContent: 'center' }}>
            Ask for a new link
          </Link>
        </div>
      </div>
    );
  return (
    <div className="login">
      {reset.isSuccess ? (
        <div className="card stack">
          <img src="/pulseiptv-stacked.png" alt="PulseIPTV" />
          <span className="reset-icon ok">
            <Check size={28} />
          </span>
          <h2 style={{ margin: 0, textAlign: 'center' }}>Password changed</h2>
          <p className="muted" style={{ margin: 0, textAlign: 'center' }}>
            You were signed out everywhere. Sign in with your new password.
          </p>
          <Link to="/login" className="btn primary" style={{ justifyContent: 'center' }}>
            Sign in
          </Link>
        </div>
      ) : (
        <form className="card stack" onSubmit={submit}>
          <img src="/pulseiptv-stacked.png" alt="PulseIPTV" />
          <h2 style={{ margin: 0 }}>Choose a new password</h2>
          <Field label="New password" hint="At least 10 characters.">
            <span className="pw-input">
              <input type={show ? 'text' : 'password'} autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} minLength={10} required autoFocus />
              <button type="button" onClick={() => setShow(!show)} title={show ? 'Hide' : 'Show'} tabIndex={-1}>
                {show ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </span>
          </Field>
          <Field label="Repeat the new password" hint={mismatch ? <span style={{ color: 'var(--danger)' }}>The passwords don't match.</span> : undefined}>
            <input type={show ? 'text' : 'password'} autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} required />
          </Field>
          <ErrorBox error={reset.error} />
          <button className="btn primary" disabled={reset.isPending || pw.length < 10 || pw !== repeat} type="submit" style={{ justifyContent: 'center' }}>
            <KeyRound size={15} /> {reset.isPending ? 'Saving…' : 'Set new password'}
          </button>
          {reset.isError && (
            <Link to="/forgot-password" className="login-link">
              Ask for a new link
            </Link>
          )}
        </form>
      )}
    </div>
  );
}
